import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchUsersData, fetchUserRoles } from "../../Redux/Thunks/usersThunk";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { getTeamsListFromApiData } from "../Teams/teamsDataUtils";
import {
  selectUsersData,
  selectUsersLoading,
  selectUsersError,
} from "../../Redux/Slices/usersSlice";
import {
  getUsersListFromApiData,
  getUsersMetaFromApiData,
  toUserIsActiveApi,
  toUserRoleApi,
} from "./usersDataUtils";
import UserList from "./UserList";
import AddUser from "./AddUser";
import EditUser from "./EditUser";
import Typography from "../../Utility/Typography";
import AddButton from "../../Utility/AddButton/AddButton";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { USERS_LIST_PAGE_SIZE_STORAGE_KEY } from "../../Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "../../hooks/useListPageSize";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const data = useSelector(selectUsersData);
  const teamsData = useSelector(selectTeamsData);
  const loading = useSelector(selectUsersLoading);
  const error = useSelector(selectUsersError);
  const { showError, errorMessage } = useAsyncResource(loading, error, data);
  const list = useMemo(() => getUsersListFromApiData(data), [data]);
  const meta = useMemo(() => getUsersMetaFromApiData(data), [data]);
  const usersData = useMemo(() => ({ list, meta }), [list, meta]);
  const teamList = useMemo(() => getTeamsListFromApiData(teamsData) ?? [], [teamsData]);
  const teamOptions = useMemo(
    () => teamList.map((t) => ({ value: t.id, label: t.teamName ?? t.name ?? String(t.id) })),
    [teamList]
  );

  const { pageSize: limit, setPageSize } = useListPageSize(USERS_LIST_PAGE_SIZE_STORAGE_KEY);
  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [roleList, setRoleList] = useState([]);
  const [listingFilter, setListingFilter] = useState({
    userName: "",
    status: "",
    role: "",
    teamIds: [],
  });
  const resolveTeamIdsForApi = useCallback((teamIds) => {
    if (!Array.isArray(teamIds) || teamIds.length === 0) return undefined;
    const ids = teamIds.map((id) => String(id).trim()).filter(Boolean);
    return ids.length > 0 ? ids : undefined;
  }, []);
  const fetchUsersWithFilters = useCallback(
    (page = 1, filterOverrides = null) => {
      const f = filterOverrides ?? listingFilter;
      const search = f.userName?.trim() || undefined;
      const role = f.role && f.role !== "All" ? toUserRoleApi(f.role, roleList) : undefined;
      const is_active = toUserIsActiveApi(f.status);
      const teamId = resolveTeamIdsForApi(f.teamIds);
      dispatch(fetchUsersData({ page, limit, search, role, is_active, teamId }));
    },
    [dispatch, limit, listingFilter, roleList, resolveTeamIdsForApi]
  );

  const wasOnChildFormRouteRef = useRef(false);

  useEffect(() => {
    const onEdit = location.pathname.startsWith("/users/edit/");
    const onAdd = location.pathname === "/users/add";
    const onChildForm = onEdit || onAdd;
    if (wasOnChildFormRouteRef.current && !onChildForm) {
      fetchUsersWithFilters(currentPage);
    }
    wasOnChildFormRouteRef.current = onChildForm;
  }, [location.pathname, currentPage, fetchUsersWithFilters]);

  useEffect(() => {
    dispatch(fetchUsersData({ page: 1, limit }));
  }, [dispatch, limit]);

  useEffect(() => {
    dispatch(fetchUserRoles())
      .unwrap()
      .then((raw) => {
        const list = Array.isArray(raw) ? raw : (raw?.data?.data ?? raw?.data ?? raw?.list ?? []);
        setRoleList(Array.isArray(list) ? list : []);
      })
      .catch(() => setRoleList([]));
  }, [dispatch]);

  useEffect(() => {
    if (!teamsData) dispatch(fetchTeamsData({ getAll: true }));
  }, [dispatch, teamsData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterReset = () => {
    setListingFilter({
      userName: "",
      status: "",
      role: "",
      teamIds: [],
    });
    setCurrentPage(1);
    dispatch(fetchUsersData({ page: 1, limit }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchUsersWithFilters(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsersWithFilters(page);
  };

  const handlePageSizeChange = (newLimit) => {
    setPageSize(newLimit);
    setCurrentPage(1);
    const f = listingFilter;
    const search = f.userName?.trim() || undefined;
    const role = f.role && f.role !== "All" ? toUserRoleApi(f.role, roleList) : undefined;
    const is_active = toUserIsActiveApi(f.status);
    const teamId = resolveTeamIdsForApi(f.teamIds);
    dispatch(fetchUsersData({ page: 1, limit: newLimit, search, role, is_active, teamId }));
  };

  if (showError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {errorMessage}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header: title + Add User button always visible (hidden on Add/Edit page) */}
      {location.pathname !== "/users/add" && !location.pathname.startsWith("/users/edit/") && (
        <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 py-3">
          <Typography variant="text1" className="text-xl font-semibold">
            Users
          </Typography>
          {can(PERMISSION_KEYS.USERS_CREATE) ? (
            <AddButton
              name="add user"
              onClick={() =>
                navigate("/users/add", {
                  state: { previousPath: location.pathname + location.search },
                })
              }
              tooltip="Add User"
            />
          ) : null}
        </div>
      )}
      {location.pathname === "/users/add" ? (
        <AddUser />
      ) : location.pathname.startsWith("/users/edit/") ? (
        <EditUser />
      ) : (
        <UserList
          usersData={usersData}
          loading={loading}
          searchTerm={searchTerm}
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          limit={limit}
          onPageSizeChange={handlePageSizeChange}
          listingFilter={listingFilter}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          onRefetchUsers={() => fetchUsersWithFilters(currentPage)}
          roleList={roleList}
          teamOptions={teamOptions}
        />
      )}
    </div>
  );
};

export default Users;
